import {
  attr,
  DependencyError,
  formGroupTemplate,
  html,
  isSupportedPdfFieldType,
  isUnusualPageSizePrecision,
  isUSLetterPage,
  PdfFieldMapping,
  printExpression,
  render,
  US_LETTER_HEIGHT,
  US_LETTER_WIDTH
} from '@cumu/shared';
import { controller, target } from '@github/catalyst';
import type { TabContainerElement } from '@github/tab-container-element';
import type { FormBehaviorElement } from '../../forms/behavior-element';
import { PdfProcessingError, processPdfInstruction } from '../../pdf';
import { dispatchChange, scrollIntoViewIfNeeded } from '../util';
import { PDFViewerBase } from './pdf-viewer-base';
import type {
  AcroFormField,
  FieldMap,
  PDFDocumentProxy,
  PDFViewerApplication
} from './pdfjs';

@controller('pdf-template')
export class PdfTemplateElement extends PDFViewerBase {
  @target declare form: HTMLFormElement;
  @target declare titleInput: HTMLInputElement;
  @target declare startPageInput: HTMLInputElement;
  @target declare endPageInput: HTMLInputElement;
  @target declare noFieldsFlash: HTMLElement;
  @target declare fieldsScript: HTMLScriptElement;
  @target declare fieldsTabPanel: HTMLElement;
  @target declare signaturesTabPanel: HTMLElement;
  @target declare tabContainer: TabContainerElement;
  acroForm: FieldMap = new Map();
  fieldsById: Record<string, AcroFormField> = {};
  fieldOutlineTimeout = 0;
  removeFieldOutline?: () => void;
  viewerOutlineTimeout = 0;
  removeViewerOutline?: () => void;
  onAnnotationEditorUiManager = undefined;
  onPdfLoaded = undefined;

  connectedCallback() {
    super.connectedCallback();
    // Fix nested scroll issues.
    addEventListener('click', () => fixScrollTop('click event'));
    addEventListener('focus', () => fixScrollTop('focus event'), {
      capture: true
    });
    this.validatePdf();

    // defer loading PDF until page is visible to workaround issue where the load intercept isn't set in time.
    // we probably need to call load explicitly instead of relying on the file url parameter in the iframe src attribute.
    const load = () =>
      this.iframe.setAttribute(
        'src',
        this.iframe.getAttribute('deferred-src')!
      );
    if (document.visibilityState === 'visible') {
      load();
    } else {
      document.addEventListener('visibilitychange', load, { once: true });
    }
  }

  async validatePdf() {
    const url = this.getAttribute('src')!;
    const response = await fetch(url);
    if (!response.ok) {
      throw new DependencyError(response, await response.text());
    }
    const data = await response.arrayBuffer();
    try {
      await processPdfInstruction({ type: 'load', data: new Uint8Array(data) });
    } catch (err) {
      if (err instanceof PdfProcessingError) {
        alert(
          `PDF file may be corrupt.\nUse https://www.ilovepdf.com/repair-pdf to repair the file.\nFailing step: ${err.userMessage}`
        );
      }
      throw err;
    }
  }

  tabChanged() {}

  onAnnotationEditorModeChanged(
    _viewerApplication: PDFViewerApplication
  ): void {}

  async onPdfDocumentLoaded(pdfDocument: PDFDocumentProxy) {
    this.log('pdf-template onPdfDocumentLoaded');
    setTimeout(this.checkPageSizes.bind(this), 100, pdfDocument);

    // If new, set title and end page.
    if (this.form.querySelector('form-behavior[new]')) {
      const { info } = await pdfDocument.getMetadata();
      this.titleInput.value = this.titleInput.value || info.Title || '';
      this.endPageInput.value = pdfDocument.numPages.toString();
      (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
    }

    // Set max page numbers.
    this.startPageInput.max = pdfDocument.numPages.toString();
    this.endPageInput.max = pdfDocument.numPages.toString();

    // Get form.
    const acroForm = await pdfDocument.getFieldObjects();
    // If there are no fields, let them know, and return.
    if (
      !acroForm ||
      !Array.from(acroForm.values()).some(fields =>
        fields.some(
          field =>
            isSupportedPdfFieldType(field.type) &&
            field.editable &&
            !field.hidden
        )
      )
    ) {
      this.noFieldsFlash.hidden = false;
      this.fieldsTabPanel.insertAdjacentHTML(
        'beforeend',
        render(
          html`<input type="hidden" name="mapping/fields:json" value="[]" />`
        )
      );
      (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
      return;
    }

    this.acroForm = acroForm;

    this.fieldsById = Array.from(acroForm.values())
      .flat()
      .reduce(
        (acc, field) => {
          acc[field.id] = field;
          return acc;
        },
        {} as Record<string, AcroFormField>
      );

    this.iframe.contentWindow?.document.documentElement.toggleAttribute(
      'cumulus--pdf-template',
      true
    );

    await this.renderMappingFields(pdfDocument);
  }

  async checkPageSizes(pdfDocument: PDFDocumentProxy) {
    const messages: string[] = [];
    for (let i = 1; i <= pdfDocument.numPages; i++) {
      const page = await pdfDocument.getPage(i);
      const { width, height } = page.getViewport({ scale: 1 });

      if (isUnusualPageSizePrecision(width, height)) {
        messages.push(
          `Page ${i} is ${width} x ${height} points (too many decimals places).`
        );
      } else if (!isUSLetterPage(width, height)) {
        messages.push(
          `Page ${i} is ${width} x ${height} points (outside tolerance).`
        );
      }
    }
    if (messages.length) {
      alert(
        `⚠️ Some pages are not US Letter size.
Expected: 8.5" x 11" (${US_LETTER_WIDTH} x ${US_LETTER_HEIGHT} points).
${messages.join('\n')}

Signature controls may not be positioned correctly when pages are irregularly sized.

Try resizing the pages using Adobe Acrobat's
[Use print production] -> [Set page sizes] tool.

If you continue to see this message, use Adobe Acrobat to create a new blank US Letter page, then copy the contents of the irregularly sized page into the new blank page. Finally, delete the irregularly sized page.`
      );
    }
  }

  onCtrlS(_viewerApplication: PDFViewerApplication): void {
    this.form.requestSubmit();
  }

  onAnnotationFieldClick(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void {
    this.handleAnnotationFieldClickOrFocus(viewerApplication, target, event);
  }

  onAnnotationFieldFocus(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void {
    this.handleAnnotationFieldClickOrFocus(viewerApplication, target, event);
  }

  async handleAnnotationFieldClickOrFocus(
    _viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    _event: Event
  ) {
    if (this.tabContainer.activePanel !== this.fieldsTabPanel) {
      this.tabContainer.selectTab(1);
      target.focus();
    }
    const id = `acro-field-${target.dataset.elementId}`;
    const element = document.getElementById(id)!;
    clearTimeout(this.fieldOutlineTimeout);
    this.removeFieldOutline?.();
    this.removeFieldOutline = undefined;
    await new Promise<void>(resolve => setTimeout(resolve, 0));
    element.classList.add('element-changed-animation');
    this.removeFieldOutline = () =>
      element.classList.remove('element-changed-animation');
    this.fieldOutlineTimeout = setTimeout(this.removeFieldOutline, 2050);
    scrollIntoViewIfNeeded(element);
    fixScrollTop('handleAnnotationClick - scrollIntoView');
  }

  onAnnotationFieldChange(
    viewerApplication: PDFViewerApplication,
    id: string
  ): void {
    const pdfDocument = viewerApplication.pdfDocument;
    if (!pdfDocument) {
      throw new Error('Expected a loaded PDF document');
    }
    // sync all fields with the same name.
    const name = this.fieldsById[id].name;
    const fields =
      this.acroForm
        .get(name)
        ?.filter(field => isSupportedPdfFieldType(field.type)) ?? [];
    for (const field of fields) {
      const initialValue = field.value;
      const override = pdfDocument.annotationStorage.getValue(field.id, {});
      // console.log(name, id, initialValue, override, field);
      const value = override?.value ?? initialValue;
      const element = document.getElementById(
        `acro-field-value-${field.id}`
      ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (!element) {
        if (this.renderingFields) {
          return;
        }
        throw new Error(`No Cumulus field for ${field.id}`);
      }
      element.value =
        typeof value === 'boolean' &&
        ['checkbox', 'radiobutton'].includes(field.type)
          ? value
            ? 'On'
            : 'Off'
          : String(value);
      dispatchChange(element);
      if (field.id === id && !this.renderingFields) {
        scrollIntoViewIfNeeded(element);
        fixScrollTop('handleAnnotationChange - scrollIntoView');
      }
    }
  }

  syncFieldMappedStatus(
    annotationInput: HTMLElement,
    printedExpression: string
  ) {
    const value = printedExpression.trim();
    annotationInput.classList.toggle('cumulus-mapped', value !== '');
    annotationInput.title = value;
  }

  async syncAcroField(event: Event) {
    if (!this.viewerApplication) {
      return;
    }
    if (event.target instanceof HTMLAnchorElement) {
      event.preventDefault();
    }
    const viewerApplication = this.viewerApplication;
    const target = event.target as HTMLElement;
    const id = target.getAttribute('element-id')!;
    const field = this.fieldsById[id];

    // render page if not already rendered
    const page = viewerApplication.pdfViewer.getPageView(field.page!);
    viewerApplication.appConfig.toolbar.pageNumber.value = String(
      field.page! + 1
    );
    viewerApplication.appConfig.toolbar.pageNumber.dispatchEvent(
      new Event('change', { bubbles: true })
    );
    // viewerApplication.eventBus.dispatch('pagenumberchanged', {
    //   source: self,
    //   value: field.page! + 1
    // });
    if (page.renderingState !== 3 /* PDFRenderingStates.FINISHED */) {
      await Promise.all([
        new Promise<void>(resolve =>
          viewerApplication.eventBus.on('pagerendered', resolve, {
            once: true,
            signal: this.detachedController?.signal
          })
        ),
        new Promise<void>(resolve =>
          viewerApplication.eventBus.on('annotationlayerrendered', resolve, {
            once: true,
            signal: this.detachedController?.signal
          })
        )
      ]);
    }

    const element = viewerApplication.appConfig.viewerContainer.querySelector(
      `[data-element-id="${id}"]`
    ) as HTMLElement;

    if (event.type === 'change') {
      this.syncFieldMappedStatus(
        element,
        (target as HTMLInputElement | HTMLTextAreaElement).value
      );
    } else {
      // click (on anchor) or expression focus/blur

      // focus (which will scroll field into view) OR only scroll into view
      if (target instanceof HTMLAnchorElement) {
        element.focus();
        fixScrollTop('syncAcroField - focus');
      } else {
        scrollIntoViewIfNeeded(element);
        fixScrollTop('syncAcroField - scrollIntoView');
      }
      // remove previous outline
      clearTimeout(this.viewerOutlineTimeout);
      this.removeViewerOutline?.();
      this.removeViewerOutline = undefined;
      if (event.type !== 'blur') {
        // outline current field
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        element.classList.add('active-field-outline');
        this.removeViewerOutline = () =>
          element.classList.remove(
            'active-field-outline',
            'active-field-outline--fade'
          );
        // enqueue outline removal
        if (target instanceof HTMLAnchorElement) {
          element.classList.add('active-field-outline--fade');
          this.viewerOutlineTimeout = setTimeout(
            this.removeViewerOutline,
            2050
          );
        }
      }
    }
  }

  onAnnotationEditorLayerRendered(
    viewerApplication: PDFViewerApplication
  ): void {
    for (const { id } of Array.from(this.acroForm.values())
      .flat()
      .filter(x => isSupportedPdfFieldType(x.type))) {
      const annotationInput =
        viewerApplication.appConfig.viewerContainer.querySelector(
          `[data-element-id="${id}"]`
        ) as HTMLElement;
      if (!annotationInput) {
        continue;
      }
      const expressionInput = this.form.querySelector<HTMLTextAreaElement>(
        `textarea[element-id="${id}"]`
      );
      if (!expressionInput) {
        continue;
      }
      this.syncFieldMappedStatus(annotationInput, expressionInput.value);
    }
  }

  renderingFields = false;
  renderMappingFields(pdfDocument: PDFDocumentProxy) {
    this.log('renderingMappingFields');
    this.renderingFields = true;
    const dataFields: PdfFieldMapping[] = JSON.parse(
      this.fieldsScript.textContent!
    );
    let templates: unknown[] = [];
    for (const name of getNamesInPositionOrder(this.acroForm)) {
      const fields =
        this.acroForm
          .get(name)
          ?.filter(field => isSupportedPdfFieldType(field.type)) ?? [];
      for (const field of fields) {
        const index = templates.length;
        const dataField =
          dataFields.find(dataField => dataField.id === field.id) ??
          dataFields.find(
            dataField =>
              dataField.name === field.name &&
              dataField.export_values === field.exportValues &&
              dataField.type === field.type
          );
        if (dataField && dataField.value !== field.value) {
          pdfDocument.annotationStorage.setValue(field.id, {
            value:
              field.type === 'checkbox' || field.type === 'radiobutton'
                ? dataField.value === 'On'
                : dataField.value
          });
        }
        const isDuplicate =
          field !==
          fields.find(
            x =>
              x.name === field.name &&
              x.exportValues === field.exportValues &&
              x.type === field.type
          );
        templates.push(
          html`<section
            id="acro-field-${field.id}"
            class="rounded-1 p-3 mt-n3 ml-n3 mb-n3 mr-n3"
          >
            <h3 class="f4 mb-2">
              ${
                field.editable === false || field.hidden === true
                  ? html`${breakAcroFieldName(field.name)}
                    ${
                      field.type === 'radiobutton' && field.exportValues
                        ? `/ ${field.exportValues}`
                        : null
                    }`
                  : html`<a
                      class="Link--primary"
                      href="#"
                      tabindex="-1"
                      element-id="${field.id}"
                      data-action="click:pdf-template#syncAcroField"
                      >${breakAcroFieldName(field.name)}${
                        field.type === 'radiobutton' && field.exportValues
                          ? ` / ${field.exportValues}`
                          : null
                      }</a
                    >`
              }
              <span class="Label Label--primary text-capitalize"
                >${field.type}</span
              >
              ${
                field.type === 'text' && field.charLimit
                  ? html`<span class="Label"
                      >${field.charLimit} characters</span
                    >`
                  : null
              }
              ${
                field.type === 'text' && field.multiline
                  ? html`<span class="Label">Multiline</span>`
                  : null
              }
              ${
                field.editable === false
                  ? html`<span class="Label Label--attention">Read only</span>`
                  : null
              }
              ${
                field.hidden === true
                  ? html`<span class="Label Label--attention">Hidden</span>`
                  : null
              }
              ${
                field.actions && Object.values(field.actions).flat().length
                  ? html`<span class="Label Label--attention">Scripted</span>`
                  : null
              }
              ${
                isDuplicate
                  ? html`<span class="Label Label--danger">Duplicate</span>`
                  : null
              }
            </h3>
            ${formGroupTemplate({
              type: 'textarea',
              label: 'Mapping',
              value: dataField?.expression
                ? printExpression(dataField.expression.ast)
                : '',
              maxLength: 3000,
              disabled: this.titleInput.disabled,
              spellcheck: false,
              rows: 2,
              name: `mapping/fields/${index}/expression:nullstring`,
              inputAttrs: [attr('element-id', field.id)],
              action:
                'focus:pdf-template#syncAcroField blur:pdf-template#syncAcroField change:pdf-template#syncAcroField',
              classes: 'position-relative',
              headerClasses: 'sr-only'
            })}
            <details class="details-reset">
              <summary class="note" tabindex="-1">Details</summary>
              <div class="d-flex flex-column gap-3 mt-2 ml-3">
                ${formGroupTemplate({
                  type: field.multiline === true ? 'textarea' : 'text',
                  label: 'Value',
                  value: dataField ? dataField.value : field.value,
                  maxLength:
                    field.type === 'text' && field.charLimit
                      ? field.charLimit
                      : 300,
                  name: `mapping/fields/${index}/value`,
                  disabled: true,
                  id: `acro-field-value-${field.id}`
                })}
                <pre><code class="p-3 f6 overflow-auto">${JSON.stringify(
                  field,
                  null,
                  2
                )}</code></pre>
              </div>
            </details>
            <input
              type="hidden"
              name="mapping/fields/${index}/name"
              value="${field.name}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/type"
              value="${field.type}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/id"
              value="${field.id}"
            />
            <input
              type="hidden"
              name="mapping/fields/${index}/page:integer"
              value="${field.page}"
            />
            ${
              field.editable === undefined
                ? null
                : html`<input
                    type="hidden"
                    name="mapping/fields/${index}/editable:boolean"
                    value="${field.editable}"
                  />`
            }
            ${
              field.hidden === undefined
                ? null
                : html`<input
                    type="hidden"
                    name="mapping/fields/${index}/hidden:boolean"
                    value="${field.hidden}"
                  />`
            }
            ${
              field.multiline === undefined
                ? null
                : html`<input
                    type="hidden"
                    name="mapping/fields/${index}/multiline:boolean"
                    value="${field.multiline}"
                  />`
            }
            ${
              field.charLimit === undefined
                ? null
                : html`<input
                    type="hidden"
                    name="mapping/fields/${index}/charlimit:integer"
                    value="${field.charLimit}"
                  />`
            }
            ${
              field.exportValues === undefined
                ? null
                : html`<input
                    type="hidden"
                    name="mapping/fields/${index}/export_values"
                    value="${field.exportValues}"
                  />`
            }
            ${
              field.defaultValue === undefined
                ? null
                : html`<input
                    type="hidden"
                    name="mapping/fields/${index}/default_value"
                    value="${field.defaultValue}"
                  />`
            }
          </section>`
        );
      }
    }
    this.fieldsTabPanel.insertAdjacentHTML('beforeend', render(templates));
    (this.form.firstElementChild as FormBehaviorElement).acceptChanges();
    this.renderingFields = false;
  }
}

function getNamesInPositionOrder(acroForm: FieldMap): string[] {
  return Array.from(acroForm.values())
    .flat()
    .filter(widget => isSupportedPdfFieldType(widget.type))
    .map(widget => ({
      page: widget.page ?? 1000, // fallback if page is missing
      name: widget.name,
      rect: widget.rect ?? [0, 0] // fallback if rect is missing
    }))
    .sort((a, b) => {
      // sort by page ascending, then Y descending (top to bottom), then X ascending (left to right)
      const dp = a.page - b.page;
      const dy = b.rect[1] - a.rect[1];
      return dp !== 0 ? dp : dy !== 0 ? dy : a.rect[0] - b.rect[0];
    })
    .map(widget => widget.name)
    .filter((x, i, a) => a.indexOf(x) === i); // remove duplicates
}

function breakAcroFieldName(name: string) {
  return name
    .trim()
    .split(/\]\./g)
    .map((x, i) => (i === 0 ? x : html`]<wbr />.${x}`));
}

function fixScrollTop(message: string) {
  if (document.documentElement.scrollTop > 0) {
    document.documentElement.scrollTop = 0;
    console.log('fixScrollTop', message);
  }
  setTimeout(fixScrollTop, 0, message + ' + timeout');
}
